/**
 * Bidirectional mappers between the server-shaped `Space` (GraphQL) and the
 * presentational `Space` (`#common/types/vault`) the UI components consume.
 *
 * The two models do NOT line up 1:1. Several UI fields have no backend home yet
 * (verified against document-vault-backend `subgraph-schema.graphql` +
 * `database/models.py`, M0 schema as of 2026-06-29). Those are handled here as
 * documented, lossy round-trips so the fields that DO persist can be wired up
 * now. Each gap is marked `NOT PERSISTED` so it's greppable when the schema grows.
 *
 * NOT PERSISTED (no backend field — dropped on write, defaulted on read):
 *   - Space.contract.{permission,qualityNote,lifecycle,ingest,channel}
 *       (backend has only a per-attribute `qualityContract` enum, no space-level
 *        contract).
 *   - Space.contract.quality — VESTIGIAL: no longer read from or written to the
 *        backend. Each attribute's `qualityContract` is now derived per-attribute
 *        from `GlobalRef.required` / `SpaceLocalAttribute.required` (see
 *        `buildAttributeAssignments`), and read returns `defaultContract()`
 *        without touching `quality`. Kept in the type only so existing UI code
 *        that still references it does not break; safe to remove once callers
 *        stop reading it.
 *   - Space.roles                       (backend access is client-scoped only)
 *   - GlobalRef.default                 (dropped from `space_attributes` in M0)
 *   - SpaceLocalAttribute.default       (same)
 *
 * PARTIAL:
 *   - Global attribute references need a backend `globalAttributeId`, but the
 *     presentational `GlobalAttribute` is keyed by an opaque `key` with no id.
 *     Writes therefore require a `globalIdByKey` lookup (see options); without
 *     it, global refs are skipped (local attributes + core fields still save).
 */
import { toBareReferenceId } from "#common/globalId";
import {
  type AttributeValueType,
  type CreateSpaceInput,
  type DomainSlug,
  type SpaceAttributeAssignmentInput,
  type UpdateSpaceInput,
} from "#graphql/supergraphTypes";
import type {
  AttrDataType,
  GlobalAttribute,
  GlobalRef,
  Space,
  SpaceContract,
  SpaceLocalAttribute,
  SpaceQuality,
} from "#common/types/vault";
import type { Space as ApiSpace } from "./hooks/useSpaces";

/* -------------------------------------------------------------------------- */
/* Domain slug <-> vault domain id                                            */
/* -------------------------------------------------------------------------- */

/**
 * Vault domain id (as used across the presentational components and mock
 * `DOMAINS`) -> backend `DomainSlug`. The backend also defines `ddq`, which has
 * no vault-domain equivalent today; unknown ids pass through unchanged.
 */
const DOMAIN_ID_TO_SLUG: Record<string, DomainSlug> = {
  fundadmin: "fund_admin",
  dex: "dex",
  fae: "faye",
  agency: "agency",
  ddq: "ddq",
};

const SLUG_TO_DOMAIN_ID: Record<DomainSlug, string> = {
  fund_admin: "fundadmin",
  dex: "dex",
  faye: "fae",
  agency: "agency",
  ddq: "ddq",
};

export const domainIdToSlug = (id: string): DomainSlug =>
  DOMAIN_ID_TO_SLUG[id] ?? (id as DomainSlug);

export const slugToDomainId = (slug: DomainSlug): string => SLUG_TO_DOMAIN_ID[slug] ?? slug;

/* -------------------------------------------------------------------------- */
/* Attribute value type <-> vault data type                                   */
/* -------------------------------------------------------------------------- */

const DATA_TYPE_TO_VALUE_TYPE: Record<AttrDataType, AttributeValueType> = {
  Text: "text",
  Number: "number",
  Date: "date",
  "Yes/No": "boolean",
  "Single-select": "single_select",
  "Multi-select": "multi_select",
  Reference: "reference",
};

const VALUE_TYPE_TO_DATA_TYPE: Record<string, AttrDataType> = {
  text: "Text",
  number: "Number",
  date: "Date",
  boolean: "Yes/No",
  single_select: "Single-select",
  multi_select: "Multi-select",
  reference: "Reference",
};

export const dataTypeToValueType = (dt: AttrDataType): AttributeValueType =>
  DATA_TYPE_TO_VALUE_TYPE[dt];

export const valueTypeToDataType = (vt: string): AttrDataType =>
  VALUE_TYPE_TO_DATA_TYPE[vt] ?? "Text";

/* -------------------------------------------------------------------------- */
/* Attribute kind helpers                                                     */
/* -------------------------------------------------------------------------- */

/** Backend `Attribute.kind` values that denote a curated global attribute. */
const isGlobalKind = (kind: string): boolean =>
  kind === "global_system" || kind === "global_steward";

const isLocalKind = (kind: string): boolean => kind === "space_local";

/** Fallback slug for a local authored without a tracked slug (mirrors SpaceEditor). */
const slugify = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

/**
 * A select attribute's option values in `displayOrder`, or `[]` for a type that
 * has none.
 *
 * Raw option `value`s, not display labels — the same list seeds the Documents-tab
 * set filter, whose `any_of` predicate the backend resolves against the
 * attribute's configured option values. Upload also reads these for local
 * Single/Multi-select dropdowns.
 */
const attributeOptionValues = (attr: ApiSpace["attributes"][number]["attribute"]): string[] =>
  [...(attr.options ?? [])].sort((a, b) => a.displayOrder - b.displayOrder).map((o) => o.value);

/**
 * Backend option id for each of an attribute's options, keyed by display
 * value — lets a picked select value resolve back to the id `valueIds`
 * requires (DV-466/DV-544). `undefined` when the attribute has no options.
 */
const attributeOptionIdsByValue = (
  attr: ApiSpace["attributes"][number]["attribute"],
): Record<string, string> | undefined =>
  attr.options?.length
    ? Object.fromEntries(attr.options.map((o) => [o.value, o.id]))
    : undefined;

/**
 * Id -> name map from `clientArenas`, keyed by the bare pk to match
 * `Space.clients`. `a.id` may arrive bare or as a relay GlobalID, so it is
 * normalised through `toBareReferenceId` rather than trusted as-is.
 */
const clientNamesFromArenas = (
  arenas: ApiSpace["clientArenas"],
): Record<string, string> | undefined =>
  arenas?.length
    ? Object.fromEntries(arenas.map((a) => [toBareReferenceId(a.id), a.name]))
    : undefined;

/** Default contract synthesized on read (no backend representation — see header). */
const defaultContract = (quality: SpaceQuality = "guaranteed"): SpaceContract => ({
  permission: "",
  quality,
  qualityNote: "",
  lifecycle: "",
  ingest: "Manual",
  channel: null,
});

/* -------------------------------------------------------------------------- */
/* Read: GraphQL Space -> vault Space                                         */
/* -------------------------------------------------------------------------- */

export interface ApiSpaceToVaultOptions {
  /**
   * The presentational global-attribute catalog (the same `globalLib` passed to
   * `DocumentVault`). Used to correlate a server attribute back to its vault
   * `key`. A server attribute matches a catalog entry when its `name`/`label`
   * equals the entry's `key` or `name`. Attributes with no match are skipped.
   */
  globalLib?: GlobalAttribute[];
}

/**
 * Map a server-shaped `Space` onto the presentational `Space` the UI renders.
 *
 * Global references are keyed back to their vault `key` via `options.globalLib`;
 * every catalog entry not referenced by the space is emitted as `{ on: false }`
 * so the Space editor shows it unchecked. See the module header for the fields
 * that are defaulted here because the backend does not store them.
 */
export const apiSpaceToVaultSpace = (
  api: ApiSpace,
  options: ApiSpaceToVaultOptions = {},
): Space => {
  const globalLib = options.globalLib ?? [];

  // Build a lookup from a server attribute's name/label to a vault key.
  const keyByServerName = new Map<string, string>();
  globalLib.forEach((g) => {
    keyByServerName.set(g.key, g.key);
    keyByServerName.set(g.name, g.key);
  });

  const globals: Record<string, GlobalRef> = {};
  // Start every catalog entry off (unchecked), then flip on the referenced ones.
  globalLib.forEach((g) => {
    globals[g.key] = { on: false, required: false, default: "" };
  });

  const locals: SpaceLocalAttribute[] = [];

  [...api.attributes]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .forEach((assignment) => {
      const attr = assignment.attribute;
      if (isLocalKind(attr.kind)) {
        locals.push({
          id: attr.id,
          // Persisted machine slug (backend `name`) vs display `label`.
          slug: attr.name,
          name: attr.label || attr.name,
          dataType: valueTypeToDataType(attr.valueType),
          // "guaranteed" = Required, "ingested" = Optional.
          required: assignment.qualityContract === "guaranteed",
          default: "",
          // Documents-tab set filter + Upload local select dropdowns — see
          // `attributeOptionValues`.
          options: attributeOptionValues(attr),
          optionIdsByValue: attributeOptionIdsByValue(attr),
        });
        return;
      }
      if (isGlobalKind(attr.kind)) {
        const key = keyByServerName.get(attr.name) ?? keyByServerName.get(attr.label);
        if (key) {
          // "guaranteed" = Required, "ingested" = Optional.
          globals[key] = {
            on: true,
            required: assignment.qualityContract === "guaranteed",
            default: "",
          };
        }
      }
    });

  return {
    id: api.id,
    name: api.name,
    domain: slugToDomainId(api.domain),
    // NOT PERSISTED: contract has no backend home — synthesized default.
    contract: defaultContract(),
    globals,
    locals,
    // appliesToAllClients === true means firm-wide (no explicit client list).
    clients: api.appliesToAllClients ? undefined : [...api.clients],
    // Carried through verbatim rather than re-derived from `clients`: a scoped
    // space with an empty client list is not firm-wide, and DV-501's controls
    // are gated on telling those two apart.
    appliesToAllClients: api.appliesToAllClients,
    // `clientArenas` can be shorter than `clients` (an unresolved federation
    // entity is dropped, not nulled), so lookups by id must tolerate misses.
    clientNames: clientNamesFromArenas(api.clientArenas),
    // NOT PERSISTED: role-scoping has no backend field.
    roles: undefined,
  };
};

/* -------------------------------------------------------------------------- */
/* Write: vault Space -> Create/Update input                                  */
/* -------------------------------------------------------------------------- */

export interface VaultSpaceToInputOptions {
  /**
   * Maps a vault global `key` to its backend `globalAttributeId`. Required to
   * persist global-attribute references; a hook exposing the backend global
   * catalog (`globalAttributes` query) is not wired up yet, so callers that
   * lack it pass nothing and global refs are omitted (local attributes and core
   * fields still save). Unresolved keys are skipped.
   */
  globalIdByKey?: Record<string, string>;
}

/**
 * Build the shared `attributeAssignments` list from a vault space.
 *
 * `qualityContract` is derived per-attribute from each attribute's `required`
 * flag: `required: true` → `"guaranteed"`, `required: false` → `"ingested"`.
 * Global refs are included only when their id resolves.
 */
const buildAttributeAssignments = (
  space: Space,
  options: VaultSpaceToInputOptions,
): SpaceAttributeAssignmentInput[] => {
  const globalIdByKey = options.globalIdByKey ?? {};
  const assignments: SpaceAttributeAssignmentInput[] = [];
  let displayOrder = 0;

  // Referenced global attributes (only those toggled on, and only if resolvable).
  Object.entries(space.globals ?? {}).forEach(([key, ref]) => {
    if (!ref?.on) return;
    const globalAttributeId = globalIdByKey[key];
    if (!globalAttributeId) return; // unresolved -> skip (see options.globalIdByKey)
    const qualityContract = ref.required ? "guaranteed" : "ingested";
    assignments.push({ globalAttributeId, qualityContract, displayOrder: displayOrder++ });
  });

  // Space-local attributes, defined inline.
  (space.locals ?? []).forEach((local) => {
    const valueType = dataTypeToValueType(local.dataType);
    const isSelect = valueType === "single_select" || valueType === "multi_select";
    const qualityContract = local.required ? "guaranteed" : "ingested";
    assignments.push({
      qualityContract,
      displayOrder: displayOrder++,
      spaceLocalAttribute: {
        // Machine `name` = the fixed slug (falls back to a slug of the display
        // name for a local authored before slugs were tracked); `label` = display.
        name: local.slug || slugify(local.name),
        label: local.name,
        valueType,
        ...(valueType === "reference" ? { referenceTarget: null } : {}),
        ...(isSelect && local.options?.length
          ? { options: local.options.map((value) => ({ value })) }
          : {}),
      },
    });
  });

  return assignments;
};

/**
 * Map a vault space to a `CreateSpaceInput`. See the module header for the
 * fields that are intentionally dropped because the backend cannot store them.
 */
export const vaultSpaceToCreateInput = (
  space: Space,
  options: VaultSpaceToInputOptions = {},
): CreateSpaceInput => {
  const appliesToAllClients = !space.clients?.length;
  return {
    name: space.name.trim(),
    domain: domainIdToSlug(space.domain),
    attributeAssignments: buildAttributeAssignments(space, options),
    appliesToAllClients,
    clients: appliesToAllClients ? null : space.clients,
    // NOT PERSISTED here: contract / roles (no backend field).
  };
};

/**
 * Map a vault space to an `UpdateSpaceInput`. `domain` is immutable on the
 * backend, so it is intentionally omitted. Attribute assignments replace the
 * current set (backend full-state semantics).
 *
 * CLIENT SCOPE IS SENT DECLARATIVELY, FROM DRAFT TRUTH (DV-738)
 * ---------------------------------------------------------------------------
 * `appliesToAllClients` and `clients` are emitted from `space` as it stands
 * now, unconditionally on every call — `addClientIds` is never sent. A caller
 * working from a stale baseline (a tab that never refetched after the space
 * was converted to all-clients elsewhere) asserts the full intended state
 * rather than describing "no change", so a stale save is rejected by the
 * backend's own guard —
 *
 * ```python
 * if (applies_to_all_clients is not _UNSET
 *     and applies_to_all_clients is False
 *     and space.applies_to_all_clients):
 *     raise SpaceValidationError("all-clients scope cannot be reversed")
 * ```
 *
 * — instead of silently no-oping or reporting success for a scope it never
 * actually asserted. The refusal reaches the user as the generic save toast
 * carrying the backend's message; there is no bespoke inline surfacing for it.
 *
 * `clients` is full-state replace, matching `vaultSpaceToCreateInput` and the
 * backend's `_apply_client_set(replace=True)` semantics — NOT additive. Client
 * removal is still out of scope for the UI (nothing here drops a saved client
 * the draft doesn't carry forward on its own), but a caller assembling
 * `space.clients` is responsible for including every client that should
 * remain, not just the ones being added.
 *
 * `space.appliesToAllClients` is coerced with `!!` rather than treated as
 * tri-state: this function's whole domain is an EXISTING space (creation goes
 * through `vaultSpaceToCreateInput`), so the flag is always populated from the
 * server data a caller started from, and `undefined` here can only be a
 * caller bug — coercing to `false` keeps the call deterministic rather than
 * silently dropping scope from it. `true` -> `clients: null`; `false` ->
 * `clients: space.clients ?? []` (including an empty list — a scoped space
 * with zero clients is unreachable via the API since DV-197's create-time
 * validator, so the backend rejecting it is correct, not a regression to
 * special-case around).
 */
export const vaultSpaceToUpdateInput = (
  spaceId: string,
  space: Space,
  options: VaultSpaceToInputOptions = {},
): UpdateSpaceInput => {
  const appliesToAllClients = !!space.appliesToAllClients;
  return {
    spaceId,
    name: space.name.trim(),
    attributeAssignments: buildAttributeAssignments(space, options),
    // domain omitted (immutable); contract / roles NOT PERSISTED.
    appliesToAllClients,
    clients: appliesToAllClients ? null : (space.clients ?? []),
  };
};
